import { logger } from '@/lib/logger';

const ACCESS_TOKEN_ROUTE = '/api/auth/access-token';
const REFRESH_BUFFER_SECONDS = 60;

interface CachedAccessToken {
  accessToken: string;
  /** JWT `exp`, seconds since epoch. */
  expiresAt: number;
}

let cachedToken: CachedAccessToken | null = null;
let pendingRequest: Promise<CachedAccessToken | null> | null = null;

const isFresh = (token: CachedAccessToken): boolean =>
  token.expiresAt - REFRESH_BUFFER_SECONDS > Math.floor(Date.now() / 1000);

const requestAccessToken = async (): Promise<CachedAccessToken | null> => {
  const response = await fetch(ACCESS_TOKEN_ROUTE, {
    credentials: 'same-origin',
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  const contentType = response.headers.get('content-type') ?? '';

  // Middleware answers an expired session with a redirect to the login page, which arrives as HTML.
  if (!response.ok || !contentType.includes('application/json')) {
    logger({
      service: 'client',
      level: 'WARN',
      body: { operation: 'requestAccessToken', status: response.status, redirected: response.redirected },
    });
    return null;
  }

  return (await response.json()) as CachedAccessToken;
};

/** Returns the host session's access token, or null when the browser has no valid session. */
export const getBrowserAccessToken = async (): Promise<string | null> => {
  if (cachedToken && isFresh(cachedToken)) {
    return cachedToken.accessToken;
  }

  pendingRequest ??= requestAccessToken().finally(() => {
    pendingRequest = null;
  });
  cachedToken = await pendingRequest;
  return cachedToken?.accessToken ?? null;
};

export const getBrowserAuthHeaders = async (): Promise<Record<string, string>> => {
  const accessToken = await getBrowserAccessToken();
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
};

export const clearBrowserAccessToken = (): void => {
  cachedToken = null;
};

/** Same-origin URLs go through the Next.js proxy, where middleware already attaches the bearer. */
export const isCrossOriginUrl = (url: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  try {
    return new URL(url, window.location.origin).origin !== window.location.origin;
  } catch {
    return false;
  }
};
